import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {
    NotificacionWeb,
    PedidoCatalogoNotificacionWebPayload,
    PedidoMenuNotificacionWebPayload
} from '@app/domain/notificacion-web';
import {PedidoTiendaOnline, TipoPedidoTiendaOnline} from '@app/domain/pedido-tienda-online';
import {PedidoTiendaOnlineService} from '@app/service/pedido-tienda-online.service';
import {LoginService} from '@app/service/login.service';

@Injectable({
    providedIn: 'root'
})
export class NotificacionWebActionService {

    constructor(private pedidoTiendaOnlineService: PedidoTiendaOnlineService,
                private loginService: LoginService,
                private messageService: MessageService,
                private router: Router) {
    }

    execute(notification: NotificacionWeb, beforeNavigate: () => void): void {
        const payload = notification.payload;
        switch (notification.codigo) {
            case 'PEDIDO_CATALOGO':
                if (!payload) {
                    this.showWarning('La notificación no contiene una acción válida.');
                    return;
                }
                this.executePedidoCatalogo(payload as PedidoCatalogoNotificacionWebPayload, beforeNavigate);
                break;
            case 'PEDIDO_MENU':
                if (!payload) {
                    this.showWarning('La notificación no contiene una acción válida.');
                    return;
                }
                this.executePedidoMenu(payload as PedidoMenuNotificacionWebPayload, beforeNavigate);
                break;
            default:
                beforeNavigate();
                break;
        }
    }

    private executePedidoCatalogo(payload: PedidoCatalogoNotificacionWebPayload,
                                  beforeNavigate: () => void): void {
        const idPedido = this.resolvePedidoId(payload);
        if (idPedido == null) {
            return;
        }
        if (!this.loginService.hasPermission('VENTAS_CREAR')) {
            this.showWarning('No cuenta con permiso para generar ventas.');
            return;
        }
        this.preparePedidoOnline(idPedido, 'CATALOGO', () => {
            beforeNavigate();
            this.router.navigateByUrl(`/ventas/nueva-venta/pedido-online/${idPedido}`);
        });
    }

    private executePedidoMenu(payload: PedidoMenuNotificacionWebPayload,
                              beforeNavigate: () => void): void {
        const idPedido = this.resolvePedidoId(payload);
        if (idPedido == null) {
            return;
        }
        this.preparePedidoOnline(idPedido, 'MENU', () => {
            beforeNavigate();
            this.router.navigate(['/restaurante/comanda/buscar-productos'], {
                queryParams: {idPedidoTiendaOnline: idPedido}
            });
        });
    }

    private preparePedidoOnline(idPedido: number,
                                expectedType: TipoPedidoTiendaOnline,
                                navigate: () => void): void {
        this.pedidoTiendaOnlineService.findById(idPedido).subscribe({
            next: pedido => this.processPedidoStatus(idPedido, pedido, expectedType, navigate)
        });
    }

    private processPedidoStatus(idPedido: number,
                                pedido: PedidoTiendaOnline,
                                expectedType: TipoPedidoTiendaOnline,
                                navigate: () => void): void {
        if (!pedido || pedido.tipo !== expectedType) {
            this.showWarning('El tipo del pedido no coincide con la notificación.');
            return;
        }

        switch (pedido.estado) {
            case 'ACEPTADO':
                navigate();
                break;
            case 'PENDIENTE':
                this.pedidoTiendaOnlineService.cambiarEstado(idPedido, 'ACEPTADO').subscribe({
                    next: () => navigate()
                });
                break;
            case 'ANULADO':
            case 'FINALIZADO':
                this.showWarning('El pedido ya no está disponible para ser procesado.');
                break;
            default:
                this.showWarning('El pedido no tiene un estado válido para ser procesado.');
                break;
        }
    }

    private resolvePedidoId(payload: PedidoCatalogoNotificacionWebPayload |
        PedidoMenuNotificacionWebPayload): number | null {
        const id = Number(payload.idPedido);
        if (!Number.isSafeInteger(id) || id <= 0) {
            this.showWarning('El payload de la notificación no contiene un identificador válido.');
            return null;
        }
        return id;
    }

    private showWarning(detail: string): void {
        this.messageService.add({
            severity: 'warn',
            summary: 'Notificación',
            detail
        });
    }
}
